/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { Typography2 } from '@coursera/cds-core';

import { usePdfViewerContext } from 'bundles/cml/shared/components/asset/pdfViewer/PdfViewerContext';

const styles = {
  sidePageCount: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    margin-top: var(--cds-spacing-200);
    margin-right: var(--cds-spacing-200);

    @media (max-width: 900px) {
      display: none;
    }
  `,
  beforeSelectedCount: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: var(--cds-color-neutral-disabled-strong);
  `,
  afterSelectedCount: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: var(--cds-color-neutral-disabled-strong);
  `,
  selectedCount: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    margin: var(--cds-spacing-50) 0;
    background: var(--cds-color-grey-50);
    border-radius: var(--cds-border-radius-50);
  `,
};

export const SidePageCount = () => {
  const verticalEllipsis = '\u22EE';

  const BEFORE_ELLIPSIS_COUNT = 6;
  const AFTER_ELLIPSIS_COUNT = 6;
  const SELECTED_ELLIPSIS_COUNT = 10;
  const SELECTED_NUM_TO_SHOW_WITH_ELLIPSIS = 3;

  const { pdf, pageStart, pageEnd } = usePdfViewerContext();

  const totalPagesInPdf = pdf?.numPages || 1;

  const startCount = pageStart;
  const endCount = pageEnd || totalPagesInPdf;

  const beforeSelectedCount = useMemo(() => {
    if (startCount === 1) {
      return [];
    } else if (startCount > BEFORE_ELLIPSIS_COUNT) {
      return [1, verticalEllipsis, startCount - 1];
    } else {
      return Array.from({ length: startCount - 1 }, (_, i) => i + 1);
    }
  }, [startCount]);

  const selectedCount = useMemo(() => {
    const numOfPagesSelected = Math.max(0, endCount - startCount);

    if (numOfPagesSelected >= SELECTED_ELLIPSIS_COUNT) {
      const beforeEllipsis = Array.from({ length: SELECTED_NUM_TO_SHOW_WITH_ELLIPSIS }, (_, i) => i + startCount);
      const afterEllipsis = Array.from({ length: SELECTED_NUM_TO_SHOW_WITH_ELLIPSIS }, (_, i) => endCount - 2 + i);

      return [...beforeEllipsis, verticalEllipsis, ...afterEllipsis];
    } else {
      return Array.from({ length: numOfPagesSelected + 1 }, (_, i) => i + startCount);
    }
  }, [startCount, endCount]);

  const afterSelectedCount = useMemo(() => {
    if (totalPagesInPdf - endCount > AFTER_ELLIPSIS_COUNT) {
      const beforeEllipsis = endCount + 1;
      const afterEllipsis = totalPagesInPdf;

      return [beforeEllipsis, verticalEllipsis, afterEllipsis];
    } else {
      return Array.from({ length: totalPagesInPdf - endCount }, (_, i) => i + endCount + 1);
    }
  }, [endCount, totalPagesInPdf]);

  return (
    <div css={styles.sidePageCount}>
      <Typography2
        component="div"
        variant="actionPrimary"
        css={styles.beforeSelectedCount}
        data-testid="before-selected-count"
      >
        {beforeSelectedCount.map((page) => (
          <div key={page}>{page}</div>
        ))}
      </Typography2>
      <Typography2
        component="div"
        variant="actionPrimary"
        color="body"
        css={styles.selectedCount}
        data-testid="selected-count"
      >
        {selectedCount.map((page) => (
          <div key={page}>{page}</div>
        ))}
      </Typography2>
      <Typography2
        component="div"
        variant="actionPrimary"
        css={styles.afterSelectedCount}
        data-testid="after-selected-count"
      >
        {afterSelectedCount.map((page) => (
          <div key={page}>{page}</div>
        ))}
      </Typography2>
    </div>
  );
};
